import { useEffect } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/types/film";

interface ArticleModalProps {
  article: Article | null;
  onClose: () => void;
}

export default function ArticleModal({ article, onClose }: ArticleModalProps) {
  useEffect(() => {
    if (!article) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = "";
    };
  }, [article, onClose]);

  if (!article) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="关闭文章"
        onClick={onClose}
        className="absolute inset-0 bg-primary-950/60 backdrop-blur-sm cursor-default"
      />
      <div className="relative z-10 max-h-[86vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-background-50">
        <div className="sticky top-0 flex items-center justify-between gap-4 border-b border-background-200 bg-background-50/95 px-5 py-4 backdrop-blur-md md:px-7">
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="rounded-sm bg-accent-100 px-2 py-0.5 text-accent-800">{article.tag}</span>
            <span className="hidden sm:inline">{article.date}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭文章"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-foreground-600 transition-colors hover:bg-background-200 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-6 md:px-7 md:py-8">
          <h2 className="font-heading text-xl font-semibold leading-snug text-foreground-950 md:text-2xl">
            {article.title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
            <span>作者 {article.author}</span>
            <span>发布时间 {article.date}</span>
            <span>评论作品《{article.filmTitle}》</span>
          </div>

          <div className="mt-6 space-y-4 text-sm leading-[1.95] text-foreground-700">
            {article.content.map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>

          <div className="mt-7 flex flex-col gap-3 rounded-md bg-background-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-foreground-600">
              本文为演示内容，观点仅代表作者个人，用于展示影评阅读体验。
            </p>
            <Link
              to={`/detail/${article.filmId}`}
              className="flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              查看《{article.filmTitle}》
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}